/**
 * History › Seasons — built to turn `15a`.
 *
 * Final standings, one season at a time, 2003 to 2025. The screen exists to answer "where did
 * everyone finish", and the drawing spends most of its space on the two things that make that
 * question harder than it looks:
 *
 *   RANK IS NOT RECORD. Final rank comes from the playoff bracket, not the regular season, so a
 *   7–7 team can finish first while a 10–4 team places third. Both columns always show, and the
 *   callout appears in the seasons where they actually disagree rather than as standing text
 *   nobody reads by the third year.
 *
 *   AN UNPAIRED TEAM IS A MANAGER, NOT A GAP. Yahoo hides manager names before 2009, leaving
 *   five teams with results and nobody's name on them. They are carried as identities of their
 *   own so the record spans the full range, and pairing one moves that season's results across
 *   in a single action.
 */

import { useMemo, useState } from 'react';
import standings from '../data/standings-history.json';
import { gmFor, managerShort } from '../domain/identity.js';
import { usePairingVersion } from './pairings.js';
import { GmBadge } from './GmBadge.js';
import { Linkify } from './RuleText.js';

interface Row {
  rank: number; team: string; gm: string | null; w: number; l: number; t: number;
  pf: number; pa: number; playoff?: boolean; gmResolved?: boolean;
}
interface SeasonFile { season: number; teams: number; standings: Row[] }

const FILE = standings as unknown as {
  source?: string; readOn?: string; seasons: SeasonFile[];
};
const SEASONS = [...FILE.seasons].sort((a, b) => b.season - a.season);

export function HistorySeasons({ onGo }: { onGo?: (section: string, page: string) => void }) {
  // Pairing a manager rewrites this screen, so it repaints when the pairing store changes.
  usePairingVersion();
  const [season, setSeason] = useState(SEASONS[0]?.season ?? 2025);
  const chosen = SEASONS.find((s) => s.season === season) ?? SEASONS[0];

  const rows = useMemo(() => (chosen?.standings ?? []).map((r) => ({
    ...r,
    manager: gmFor(chosen!.season, r),
  })), [chosen]);

  /**
   * Does this season's bracket disagree with its regular season? The callout is drawn as a
   * standing explanation; showing it only when the columns actually contradict each other keeps
   * it worth reading — and it is the champion's own record that makes the point, so the text
   * names the two teams involved rather than describing the rule in the abstract.
   */
  const disagreement = useMemo(() => {
    if (rows.length === 0) return null;
    const champion = rows.find((r) => r.rank === 1);
    const best = [...rows].sort((a, b) => (b.w - b.l) - (a.w - a.l))[0];
    if (!champion || !best || best.rank === 1) return null;
    return { champion, best };
  }, [rows]);

  /** Every team across every season the league still cannot put a name to. */
  const unpaired = useMemo(() => SEASONS.flatMap((s) => s.standings
    .filter((r) => !gmFor(s.season, r))
    .map((r) => ({ season: s.season, team: r.team }))), []);

  const fmt = (n: number) => n.toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <div className="app">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Seasons</h1>
            <p className="sub">
              Final standings, {SEASONS[SEASONS.length - 1]?.season} to {SEASONS[0]?.season}
            </p>
          </div>
          <label className="yearpick">
            <span className="mut">Season</span>
            <select value={season} onChange={(e) => setSeason(Number(e.target.value))}>
              {SEASONS.map((s) => <option key={s.season} value={s.season}>{s.season}</option>)}
            </select>
          </label>
        </header>

        <section className="card standingscard">
          <table className="standings">
            <thead>
              <tr>
                <th className="rk">Rk</th>
                <th>Team</th>
                <th>Manager</th>
                <th className="num">W–L</th>
                <th className="num">PF</th>
                <th className="num">PA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.rank}-${r.team}`} className={r.rank <= 3 ? 'podium' : ''}>
                  <td className="rk">{r.rank}</td>
                  <td className="team">{r.team}</td>
                  <td className="mgr">
                    {r.manager
                      ? <><GmBadge gm={r.manager} /> {managerShort(r.manager)}</>
                      : <span className="unpairedmark">not paired</span>}
                  </td>
                  <td className="num">{r.w}–{r.l}{r.t ? `–${r.t}` : ''}</td>
                  <td className="num">{fmt(r.pf)}</td>
                  <td className="num">{fmt(r.pa)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {disagreement && (
          <section className="card ranknotrecord">
            <p>
              <b>{disagreement.champion.manager
                ? managerShort(disagreement.champion.manager)
                : disagreement.champion.team}</b>
              {' '}finished first at {disagreement.champion.w}–{disagreement.champion.l} while
              {' '}<b>{disagreement.best.manager
                ? managerShort(disagreement.best.manager)
                : disagreement.best.team}</b>
              {' '}went {disagreement.best.w}–{disagreement.best.l} and placed
              {' '}{ordinal(disagreement.best.rank)}. Final rank comes from the playoff bracket,
              not the regular season, so the two columns disagree by design — and every season
              table in the app shows both rather than implying one explains the other.
            </p>
          </section>
        )}

        <div className="histfoot">
          <section className="card unpairedcard">
            <div className="striptop">
              <h3 className="striphead">Unpaired managers</h3>
              <span className="mut">{unpaired.length} unpaired team{unpaired.length === 1 ? '' : 's'}</span>
            </div>
            {unpaired.length > 0 ? (
              <>
                <p className="note">
                  {unpaired.length} team{unpaired.length === 1 ? ' has' : 's have'} results but no
                  manager the league can name. Each is carried as a standalone manager under its
                  own team name, so totals span the full range.
                </p>
                <div className="idchips">
                  {unpaired.map((u) => (
                    <span className="idchip" key={`${u.season}-${u.team}`}>
                      <span className="yr">{u.season}</span>{u.team}
                    </span>
                  ))}
                </div>
                {onGo && (
                  <p className="note">
                    <button type="button" className="linkbtn"
                      onClick={() => onGo('history', 'managers')}>
                      Pair an identity
                    </button>
                    {' '}— pairing one moves that season&rsquo;s results across in a single action.
                  </p>
                )}
              </>
            ) : (
              <p className="note ok">
                Every team from {SEASONS[SEASONS.length - 1]?.season} to {SEASONS[0]?.season} is
                paired to a named manager, so career totals cover the full range.
              </p>
            )}
          </section>

          <div className="histaside">
            <section className="card">
              <h3 className="striphead">Team names move</h3>
              <p className="note">
                A GM renames his team most years, so a name is not an identity. Every table keys
                on the manager and shows the team name of that season beside it.
              </p>
              {rows[2] && rows[2].manager && (
                <p className="note">
                  {managerShort(rows[2].manager)} — <b>{rows[2].team}</b> · {season}
                </p>
              )}
            </section>

            <section className="card">
              <h3 className="striphead">Source</h3>
              <p className="note">
                {FILE.source ?? 'Yahoo'}
                {FILE.readOn ? `, read on ${FILE.readOn}` : ''}. Season results are complete;
                only the manager pairings above are outstanding.
              </p>
              <p className="note"><code>standings-history.json</code></p>
            </section>
          </div>
        </div>
      </Linkify>
    </div>
  );
}

const ordinal = (n: number) => {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};
